import React, { useState, useEffect, useRef } from "react";
import aboutPageBg from "../assests/aboutPage.png";
import "./AboutPage.css";
import { assets } from "../assests/assests";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import Footer from "../Component/Footer";



const sliderImages = [
  assets.slider1,
  aboutPageBg,
];

function AboutPage() {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev === sliderImages.length - 1 ? 0 : prev + 1));
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="bg-white min-h-screen font-sans">
      {/* Top Image Slider */}
      <div className="w-full h-[300px] md:h-[400px] relative overflow-hidden bg-black flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentSlide}
            src={sliderImages[currentSlide]}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className="absolute inset-0 w-full h-full object-cover"
            alt={`Slide ${currentSlide + 1}`}
          />
        </AnimatePresence>
        
        {/* Dots Indicator */}
        <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-10">
          {sliderImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                index === currentSlide ? "bg-white" : "bg-white/50 hover:bg-white/80"
              }`}
            />
          ))}
        </div>
      </div>

      <div className="w-full px-6 md:px-12 lg:px-20 py-12 max-w-[1600px] mx-auto">
        
        {/* Section 1: Our Startup Journey (Ultra Simple NPTEL Style) */}
        <div className="mb-20">
          <h2 className="text-[28px] text-gray-900 mb-6 font-normal">Our Startup Journey</h2>
          
          <div className="text-[#333333] text-[16px] leading-[1.6] space-y-5 font-sans">
            <p>Our startup journey began with a simple but powerful vision: to use technology to solve real problems faced by farmers and make agriculture smarter, more efficient, and sustainable.</p>
            <p>We started by identifying major challenges in agriculture, especially the lack of accessible soil health information, inefficient fertilizer and pesticide usage, and the difficulty farmers face in adopting advanced technologies. This motivated us to build practical, affordable, and farmer-friendly solutions rather than developing technology only for demonstration.</p>
            <p>Our initial work focused on developing a portable soil health monitoring system using spectroscopy and embedded electronics. We then expanded our vision toward agricultural automation and developed an unmanned ground rover capable of performing precision spraying operations. Through continuous research and prototyping, we developed our first MVP and began working on an advanced version that uses soil testing data to calculate fertilizer requirements and support precision application.</p>
            <p>Alongside the rover, we explored AI-based crop health monitoring using drones and computer vision, enabling identification of unhealthy crops and supporting targeted spraying. We also started developing intelligent farmer advisory solutions to help farmers understand their soil and crop requirements in a simple and accessible way.</p>
            <p>Our journey has involved numerous challenges—hardware failures, field-testing difficulties, software bugs, mechanical redesigns, limited resources, and the continuous process of converting research ideas into reliable products. Each challenge became an opportunity to improve our technology.</p>
            <p>We have received support through incubation, grants, mentorship, academic collaborations, and industry interactions. These opportunities helped us strengthen our R&D capabilities, build prototypes, conduct field validation, and move closer to commercialization.</p>
            <p>Today, our vision is much broader than a single product. We are building an integrated precision-agriculture ecosystem combining soil intelligence, AI, robotics, drones, automation, and farmer advisory services.</p>
            <p>Our journey is still at an early stage, but our goal remains clear: to make advanced agricultural technology practical, affordable, and accessible to farmers, while helping them increase productivity, reduce input wastage, and make data-driven farming decisions.</p>
            <p>From an idea to prototypes, from prototypes to field testing, and from field testing toward scalable products—this is our startup journey.</p>
          </div>
        </div>

        {/* Section 2: Meet Our Team */}
        <div className="mb-20">
          <h2 className="text-[32px] text-gray-900 mb-12 font-bold text-center">Meet Our Team</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 justify-center">
            {/* CEO */}
            <motion.div whileHover={{ y: -8 }} className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] border border-gray-100 overflow-hidden flex flex-col transition-all duration-300">
              <div className="h-64 w-full bg-gray-50 relative overflow-hidden">
                <img src={assets.karan} alt="Karan - CEO" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6 text-center flex-1 flex flex-col">
                <h3 className="text-xl font-bold text-gray-900 mb-1">Karan</h3>
                <p className="text-green-600 font-semibold text-sm mb-2">Managing Director & CEO</p>
                <p className="text-gray-500 text-xs mb-4 px-2 py-1 bg-gray-50 rounded-md inline-block self-center border border-gray-100">B.Tech Graduate</p>
                <a href="mailto:nahatakaran52@gmail.com" className="text-xs text-blue-600 hover:text-blue-800 transition-colors mt-auto">nahatakaran52@gmail.com</a>
              </div>
            </motion.div>

            {/* Software Developer (VS Rathore) */}
            <motion.div whileHover={{ y: -8 }} className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] border border-gray-100 overflow-hidden flex flex-col transition-all duration-300">
              <div className="h-64 w-full bg-gray-100 relative overflow-hidden">
                <img src={assets.vsrathore} alt="VS Rathore" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6 text-center flex-1 flex flex-col">
                <h3 className="text-xl font-bold text-gray-900 mb-1">VS Rathore</h3>
                <p className="text-blue-600 font-semibold text-sm mb-2">Software Developer</p>
                <p className="text-gray-500 text-xs mb-4 px-2 py-1 bg-gray-50 rounded-md inline-block self-center border border-gray-100">B.Tech 3rd Year</p>
                <a href="mailto:vickybanna1236@gmail.com" className="text-xs text-blue-600 hover:text-blue-800 transition-colors mt-auto">vickybanna1236@gmail.com</a>
              </div>
            </motion.div>

            {/* Team Member 1 */}
            <motion.div whileHover={{ y: -8 }} className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] border border-gray-100 overflow-hidden flex flex-col transition-all duration-300">
              <div className="h-64 w-full bg-gray-50 flex items-center justify-center relative overflow-hidden">
                <img src={assets.userImage} alt="Animesh" className="w-24 h-24 opacity-30 hover:scale-110 transition-transform duration-500" />
              </div>
              <div className="p-6 text-center flex-1 flex flex-col justify-center">
                <h3 className="text-xl font-bold text-gray-900 mb-1">Animesh</h3>
                <p className="text-gray-500 text-sm font-medium">Core Team Member</p>
              </div>
            </motion.div>

            {/* Team Member 2 */}
            <motion.div whileHover={{ y: -8 }} className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] border border-gray-100 overflow-hidden flex flex-col transition-all duration-300">
              <div className="h-64 w-full bg-gray-50 flex items-center justify-center relative overflow-hidden">
                <img src={assets.userImage} alt="Mahi" className="w-24 h-24 opacity-30 hover:scale-110 transition-transform duration-500" />
              </div>
              <div className="p-6 text-center flex-1 flex flex-col justify-center">
                <h3 className="text-xl font-bold text-gray-900 mb-1">Mahi</h3>
                <p className="text-gray-500 text-sm font-medium">Core Team Member</p>
              </div>
            </motion.div>

            {/* Team Member 3 */}
            <motion.div whileHover={{ y: -8 }} className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] border border-gray-100 overflow-hidden flex flex-col transition-all duration-300">
              <div className="h-64 w-full bg-gray-50 flex items-center justify-center relative overflow-hidden">
                <img src={assets.userImage} alt="Manish" className="w-24 h-24 opacity-30 hover:scale-110 transition-transform duration-500" />
              </div>
              <div className="p-6 text-center flex-1 flex flex-col justify-center">
                <h3 className="text-xl font-bold text-gray-900 mb-1">Manish</h3>
                <p className="text-gray-500 text-sm font-medium">Core Team Member</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default AboutPage;


